import React, { useState, useCallback, useEffect, useRef } from 'react';
import InspectionChecklist from './InspectionChecklist';
import PhotoUpload from './PhotoUpload';
import DefectReporting from './DefectReporting';
import DigitalSignature from './DigitalSignature';
import { Truck, Clock, User, Calendar, ChevronLeft, ChevronRight, Check, Loader } from 'lucide-react';
import inspectionService from '../../services/inspectionService';
import { useAuth } from '../../contexts/AuthContext';

const normalizeEstimatedCostValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = value.toString().trim();
  if (!trimmed) {
    return null;
  }

  const cleaned = trimmed.replace(/[^0-9.-]/g, '');
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const sanitizeDefect = (defect = {}) => {
  if (!defect || typeof defect !== 'object') {
    return {};
  }

  const sanitized = { ...defect };
  const normalizedCost = normalizeEstimatedCostValue(defect.estimatedCost);

  if (normalizedCost === null) {
    delete sanitized.estimatedCost;
  } else {
    sanitized.estimatedCost = normalizedCost;
  }

  return sanitized;
};

const buildDefectPayload = (defect = {}) => {
  const payload = sanitizeDefect(defect);
  delete payload.id;
  delete payload.uploadFailed;
  delete payload.error;
  delete payload.localOnly;
  return payload;
};

const VehicleInspection = ({ vehicleId, inspectionType = 'pre-trip', onInspectionComplete }) => {
  const { company, userData } = useAuth();
  const initialSection = inspectionType === 'load-inspection' ? 'photos' : 'checklist';
  const [activeSection, setActiveSection] = useState(initialSection);
  const [visitedSections, setVisitedSections] = useState(new Set([initialSection]));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inspectionId, setInspectionId] = useState(null);
  const [inspectionData, setInspectionData] = useState({
    vehicleId: vehicleId,
    inspectorId: userData?.uid, // Current user is the inspector
    inspectionType,
    timestamp: new Date(),
    checklist: {},
    photos: [],
    defects: [],
    signature: null,
    status: 'pending'
  });
  const uploadedPhotoIdsRef = useRef(new Set());

  // Initialize inspection when component mounts
  useEffect(() => {
    const createInspection = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Wait for company and user data to be available
        if (!company?.id || !userData?.uid) {
          console.log('Waiting for company and user data...');
          return;
        }
        
        // Validate required parameters
        if (!vehicleId) {
          throw new Error('Vehicle ID is required to start inspection');
        }
        if (!userData?.uid) {
          throw new Error('User must be logged in to start inspection');
        }

        const inspection = await inspectionService.createInspection({
          vehicleId: vehicleId,
          inspectorId: userData?.uid, // Current user performing the inspection
          companyId: company?.id,
          inspectionType,
          mileage: 0,
          engineHours: 0,
        });

        setInspectionId(inspection.id);
        setInspectionData(prev => ({
          ...prev,
          inspectionId: inspection.id,
          inspectionNumber: inspection.inspectionNumber,
          status: inspection.status,
        }));
      } catch (error) {
        console.error('Failed to create inspection:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    createInspection();
  }, [vehicleId, inspectionType, company?.id, userData?.uid]);

  const handleChecklistChange = useCallback((checklistData) => {
    setInspectionData(prev => ({
      ...prev,
      checklist: checklistData
    }));
  }, []);

  const handlePhotoUpload = useCallback(async (photos = []) => {
    if (!inspectionId) {
      console.error('No inspection ID available for photo upload');
      return;
    }

    const photosArray = Array.isArray(photos) ? photos : [];
    const photosToUpload = photosArray.filter((photo) => {
      if (!photo || !photo.id) return false;
      if (uploadedPhotoIdsRef.current.has(photo.id)) return false;
      return !!photo.file;
    });

    if (photosToUpload.length === 0) {
      return;
    }

    try {
      const uploadedPhotos = [];
      for (const photo of photosToUpload) {
        try {
          const uploadedPhoto = await inspectionService.uploadPhoto(inspectionId, photo);
          uploadedPhotos.push({
            ...photo,
            id: uploadedPhoto.id,
            url: uploadedPhoto.url,
            thumbnailUrl: uploadedPhoto.thumbnailUrl,
            uploadedAt: uploadedPhoto.uploadedAt,
          });
          uploadedPhotoIdsRef.current.add(photo.id);
        } catch (error) {
          console.error('Failed to upload photo:', error);
        }
      }

      if (uploadedPhotos.length > 0) {
        setInspectionData(prev => ({
          ...prev,
          photos: [...prev.photos, ...uploadedPhotos]
        }));
      }
    } catch (error) {
      console.error('Error in photo upload handler:', error);
    }
  }, [inspectionId]);

  const handleDefectReport = useCallback(async (defects) => {
    if (!inspectionId) {
      console.error('No inspection ID available for defect reporting');
      return;
    }

    try {
      const isPersistedDefect = (defect) => {
        if (!defect?.id) return false;
        return !defect.id.toString().startsWith('defect_');
      };

      const newDefects = defects.filter(defect => !isPersistedDefect(defect));
      const existingDefects = defects.filter(isPersistedDefect).map(sanitizeDefect);

      // Upload new defects to the API
      const uploadedDefects = [];
      for (const defect of newDefects) {
        try {
          const uploadedDefect = await inspectionService.addDefect(inspectionId, buildDefectPayload(defect));
          uploadedDefects.push({
            ...sanitizeDefect(defect),
            id: uploadedDefect.id,
            detectedAt: uploadedDefect.detectedAt,
          });
        } catch (error) {
          console.error('Failed to upload defect:', error);
          // Still add the defect to local state even if upload fails
          uploadedDefects.push({
            ...sanitizeDefect(defect),
            uploadFailed: true,
            error: error.message,
          });
        }
      }

      setInspectionData(prev => ({
        ...prev,
        defects: [...existingDefects, ...uploadedDefects]
      }));
    } catch (error) {
      console.error('Error in defect report handler:', error);
    }
  }, [inspectionId]);

  const handleSignatureCapture = useCallback(async (signatureData) => {
    if (!inspectionId) {
      console.error('No inspection ID available for completion');
      return;
    }

    try {
      setIsLoading(true);
      
      // Determine if inspection passed based on defects
      const criticalDefects = inspectionData.defects.filter(defect => 
        defect.severity === 'critical'
      );
      const passed = criticalDefects.length === 0;

      const completionData = {
        signature: signatureData,
        signedBy: userData ? `${userData.firstName} ${userData.lastName}` : 'Inspector', // Inspector (current user) signs
        checklist: inspectionData.checklist,
        defects: (inspectionData.defects || []).map(sanitizeDefect), // Include defects in completion data
        passed,
        notes: `${inspectionType} inspection ${passed ? 'passed' : 'failed'}${
          criticalDefects.length > 0 ? ` with ${criticalDefects.length} critical defect(s)` : ''
        }`,
        location: 'Inspection completed', // Static location description
        coordinates: null, // No location coordinates required
        mileage: 0, // Could be improved with actual mileage input
        engineHours: 0, // Could be improved with actual engine hours input
      };

      const completedInspection = await inspectionService.completeInspection(
        inspectionId, 
        completionData
      );

      const finalInspectionData = {
        ...inspectionData,
        signature: signatureData,
        status: 'completed',
        completedAt: new Date(),
        passed,
        scores: completedInspection.scores,
        nextInspectionDue: completedInspection.nextInspectionDue,
      };
      
      setInspectionData(finalInspectionData);
      
      // Call the completion callback if provided
      if (onInspectionComplete) {
        onInspectionComplete(finalInspectionData);
      }
    } catch (error) {
      console.error('Failed to complete inspection:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [inspectionId, inspectionData, inspectionType, onInspectionComplete]);

  // Validation functions
  const getChecklistCompletion = () => {
    const checklist = inspectionData.checklist || {};
    const totalItems = Object.keys(checklist).length;
    
    // Define the expected checklist items based on inspection type
    const preTrip = [
      'engine_oil', 'coolant_level', 'brake_fluid', 'power_steering', 'belt_condition', 'battery_terminals',
      'tire_condition', 'wheel_lugs', 'mirrors_clean', 'lights_functioning', 'horn_working', 'wipers_condition',
      'steering_wheel', 'seat_belts', 'parking_brake', 'service_brakes', 'emergency_equipment', 'fire_extinguisher'
    ];
    
    const postTrip = [
      'damage_exterior', 'damage_interior', 'fluid_leaks', 'unusual_noises',
      'engine_performance', 'brake_performance', 'steering_issues', 'transmission_issues',
      'fuel_level', 'mileage_recorded', 'interior_clean', 'logbook_updated'
    ];
    
    const expectedItems = inspectionType === 'pre-trip' ? preTrip : postTrip;
    const completedItems = expectedItems.filter(itemId => 
      checklist[itemId] && checklist[itemId].status && checklist[itemId].status !== 'unchecked'
    ).length;
    
    // Checklist is complete if at least 50% of items are completed (flexible threshold)
    const completionThreshold = Math.ceil(expectedItems.length * 0.5);
    const completed = completedItems >= completionThreshold;
    
    return { 
      completed, 
      count: completedItems,
      total: expectedItems.length,
      percentage: Math.round((completedItems / expectedItems.length) * 100)
    };
  };

  const getPhotosCompletion = () => {
    // Define required categories based on inspection type
    const requiredCategories = inspectionType === 'load-inspection'
      ? ['front_plate', 'back', 'left_side', 'right_side', 'trailer'] // All 5 categories required for load inspection
      : ['exterior_front', 'exterior_back', 'dashboard']; // Regular inspection categories

    const photos = inspectionData.photos || [];
    const categoriesWithPhotos = [...new Set(photos.map(p => p.category))];
    const requiredCompleted = requiredCategories.filter(cat =>
      categoriesWithPhotos.includes(cat)
    ).length;

    // For load inspection, all 5 categories are required
    // For regular inspections, at least 1 of 3 required categories
    const isComplete = inspectionType === 'load-inspection'
      ? requiredCompleted === requiredCategories.length
      : requiredCompleted >= 1;

    return {
      completed: isComplete,
      count: photos.length,
      requiredCompleted,
      requiredTotal: requiredCategories.length
    };
  };

  const getDefectsCompletion = () => {
    // Defects are optional, but only marked complete if section has been visited
    const hasVisited = visitedSections.has('defects');
    return { 
      completed: hasVisited, 
      count: inspectionData.defects?.length || 0 
    };
  };

  const getSignatureCompletion = () => {
    return { completed: !!inspectionData.signature, present: !!inspectionData.signature };
  };

  const getSectionCompletion = (sectionId) => {
    switch (sectionId) {
      case 'checklist': return getChecklistCompletion();
      case 'photos': return getPhotosCompletion();
      case 'defects': return getDefectsCompletion();
      case 'signature': return getSignatureCompletion();
      default: return { completed: false };
    }
  };

  const canCompleteInspection = () => {
    const photos = getPhotosCompletion();

    // For load-inspection, only photos are required
    if (inspectionType === 'load-inspection') {
      console.log('Load Inspection Completion Status:', {
        photos: photos,
        canComplete: photos.completed
      });
      return photos.completed;
    }

    // For other inspections, require both checklist and photos
    const checklist = getChecklistCompletion();
    console.log('Completion Status:', {
      checklist: checklist,
      photos: photos,
      canComplete: checklist.completed && photos.completed
    });
    return checklist.completed && photos.completed;
  };

  // Define sections based on inspection type
  const sections = inspectionType === 'load-inspection'
    ? [
        { id: 'photos', name: 'Photos', icon: '📷' },
        { id: 'signature', name: 'Sign', icon: '✍️' }
      ]
    : [
        { id: 'checklist', name: 'Checklist', icon: '✓' },
        { id: 'photos', name: 'Photos', icon: '📷' },
        { id: 'defects', name: 'Defects', icon: '⚠️' },
        { id: 'signature', name: 'Sign', icon: '✍️' }
      ];

  // Navigation helpers
  const getCurrentSectionIndex = () => sections.findIndex(s => s.id === activeSection);
  const canGoToNext = () => {
    const currentIndex = getCurrentSectionIndex();
    const nextSection = sections[currentIndex + 1];
    
    // Can't go beyond last section
    if (!nextSection) return false;
    
    // Can go to signature only if inspection requirements are met
    if (nextSection.id === 'signature') {
      return canCompleteInspection();
    }
    
    return true;
  };
  
  const canGoToPrevious = () => getCurrentSectionIndex() > 0;
  
  const goToNext = () => {
    if (!canGoToNext()) return;
    
    const currentIndex = getCurrentSectionIndex();
    const nextSection = sections[currentIndex + 1];
    
    setActiveSection(nextSection.id);
    setVisitedSections(prev => new Set([...prev, nextSection.id]));
  };
  
  const goToPrevious = () => {
    if (!canGoToPrevious()) return;
    
    const currentIndex = getCurrentSectionIndex();
    const prevSection = sections[currentIndex - 1];
    
    setActiveSection(prevSection.id);
    setVisitedSections(prev => new Set([...prev, prevSection.id]));
  };

  const getNextButtonText = () => {
    const currentIndex = getCurrentSectionIndex();
    const nextSection = sections[currentIndex + 1];
    
    if (!nextSection) return 'Complete';
    if (nextSection.id === 'signature') return 'Ready to Sign';
    return `Next: ${nextSection.name}`;
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'checklist':
        return (
          <InspectionChecklist
            inspectionType={inspectionData.inspectionType}
            onChecklistChange={handleChecklistChange}
            initialData={inspectionData.checklist}
          />
        );
      case 'photos':
        return (
          <PhotoUpload
            onPhotoUpload={handlePhotoUpload}
            existingPhotos={inspectionData.photos}
            vehicleId={inspectionData.vehicleId}
            inspectionType={inspectionType}
          />
        );
      case 'defects':
        return (
          <DefectReporting
            onDefectReport={handleDefectReport}
            existingDefects={inspectionData.defects}
            photos={inspectionData.photos}
          />
        );
      case 'signature':
        const canComplete = canCompleteInspection();
        return (
          <div className="space-y-6">
            {!canComplete && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Complete Required Sections First
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Please complete the following before signing:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        {!getChecklistCompletion().completed && (
                          <li>
                            Fill out the inspection checklist
                            <button 
                              onClick={() => {
                                setActiveSection('checklist');
                                setVisitedSections(prev => new Set([...prev, 'checklist']));
                              }}
                              className="ml-2 text-xs text-yellow-800 hover:text-yellow-900 underline"
                            >
                              Go to Checklist
                            </button>
                          </li>
                        )}
                        {!getPhotosCompletion().completed && (
                          <li>
                            Take photos for at least 2 required categories (Front, Back, Dashboard)
                            <button 
                              onClick={() => {
                                setActiveSection('photos');
                                setVisitedSections(prev => new Set([...prev, 'photos']));
                              }}
                              className="ml-2 text-xs text-yellow-800 hover:text-yellow-900 underline"
                            >
                              Go to Photos
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DigitalSignature
              onSignatureCapture={handleSignatureCapture}
              inspectionData={inspectionData}
              existingSignature={inspectionData.signature}
              disabled={!canComplete}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const NavigationButtons = () => (
    <div className="bg-white border-t border-slate-200 px-3 sm:px-4 py-3">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {/* Previous Button */}
        <button
          onClick={goToPrevious}
          disabled={!canGoToPrevious()}
          className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            canGoToPrevious()
              ? 'text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300'
              : 'text-slate-400 bg-slate-50 border border-slate-200 cursor-not-allowed'
          }`}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>

        {/* Section Indicator */}
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <span>Step {getCurrentSectionIndex() + 1} of {sections.length}</span>
          <span className="hidden sm:inline">•</span>
          <span className="hidden sm:inline">{sections[getCurrentSectionIndex()]?.name}</span>
        </div>

        {/* Next Button */}
        <button
          onClick={goToNext}
          disabled={!canGoToNext()}
          className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            canGoToNext()
              ? 'text-white bg-primary-600 hover:bg-primary-700'
              : 'text-slate-400 bg-slate-50 border border-slate-200 cursor-not-allowed'
          }`}
        >
          {getNextButtonText()}
          {canGoToNext() && <ChevronRight className="h-4 w-4 ml-1" />}
        </button>
      </div>
    </div>
  );


  // Show loading state while creating inspection
  if (isLoading && !inspectionId) {
    return (
      <div className="h-full flex flex-col bg-slate-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Starting Inspection</h3>
            <p className="text-sm text-slate-600">Creating inspection record...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if inspection creation failed
  if (error && !inspectionId) {
    return (
      <div className="h-full flex flex-col bg-slate-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-red-100 rounded-full p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Failed to Start Inspection</h3>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Loading overlay for API operations */}
      {isLoading && inspectionId && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
            <p className="text-sm text-slate-600">Saving inspection...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <Truck className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600" />
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-slate-900 capitalize">
                  {inspectionData.inspectionType} Inspection
                </h1>
                <div className="text-xs sm:text-sm text-slate-500">
                  <p>Vehicle {inspectionData.vehicleId}</p>
                  {inspectionData.inspectionNumber && (
                    <p>Inspection #{inspectionData.inspectionNumber}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs sm:text-sm text-slate-600">
              <div className="flex items-center">
                <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="truncate">
                  Inspector: {userData?.firstName} {userData?.lastName}
                </span>
              </div>
              <div className="flex items-center">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="truncate">{inspectionData.timestamp.toLocaleDateString()}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="truncate">{inspectionData.timestamp.toLocaleTimeString()}</span>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="flex space-x-1">
            {sections.map((section, index) => {
              const completion = getSectionCompletion(section.id);
              const isActive = activeSection === section.id;
              const isPassed = index < sections.findIndex(s => s.id === activeSection);
              
              return (
                <div
                  key={section.id}
                  className={`flex-1 h-1.5 sm:h-2 rounded-full ${
                    isActive
                      ? 'bg-primary-600'
                      : completion.completed
                      ? 'bg-green-400'
                      : isPassed
                      ? 'bg-yellow-400'
                      : 'bg-slate-200'
                  }`}
                />
              );
            })}
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav className="flex overflow-x-auto scrollbar-hide">
          {sections.map((section) => {
            const completion = getSectionCompletion(section.id);
            const isActive = activeSection === section.id;
            const canAccess = section.id === 'signature' ? canCompleteInspection() : true;
            
            const handleSectionClick = () => {
              if (section.id === 'signature' && !canAccess) return;
              
              setActiveSection(section.id);
              setVisitedSections(prev => new Set([...prev, section.id]));
            };
            
            return (
              <button
                key={section.id}
                onClick={handleSectionClick}
                disabled={section.id === 'signature' && !canAccess}
                className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap min-w-0 relative ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : section.id === 'signature' && !canAccess
                    ? 'border-transparent text-slate-400 cursor-not-allowed'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="text-sm sm:text-base">{section.icon}</span>
                <span className="hidden xs:inline sm:inline">{section.name}</span>
                {completion.completed && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-4">
          {renderActiveSection()}
        </div>
      </div>

      {/* Navigation Buttons */}
      <NavigationButtons />

      {/* Status Bar */}
      <div className="bg-white border-t border-slate-200 px-3 sm:px-4 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
              inspectionData.status === 'completed'
                ? 'bg-green-100 text-green-800'
                : canCompleteInspection()
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {inspectionData.status === 'completed' 
                ? 'Completed' 
                : canCompleteInspection() 
                ? 'Ready to Sign' 
                : 'In Progress'
              }
            </span>
            <span className={`text-xs sm:text-sm ${
              getChecklistCompletion().completed ? 'text-green-600' : 'text-slate-600'
            }`}>
              {Object.keys(inspectionData.checklist).length} items
              {getChecklistCompletion().completed && ' ✓'}
            </span>
            <span className={`text-xs sm:text-sm ${
              getPhotosCompletion().completed ? 'text-green-600' : 'text-slate-600'
            }`}>
              {inspectionData.photos.length} photos
              {getPhotosCompletion().completed && ' ✓'}
            </span>
            <span className="text-xs sm:text-sm text-slate-600">
              {inspectionData.defects.length} defects
            </span>
          </div>
          <div className="flex items-center space-x-3 self-start sm:self-auto">
            {canCompleteInspection() && !inspectionData.signature && (
              <span className="text-xs sm:text-sm text-blue-600 font-medium">
                → Sign to Complete
              </span>
            )}
            {inspectionData.signature && (
              <span className="text-xs sm:text-sm text-green-600 font-medium">
                ✓ Signed
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleInspection;
